import { ref, onMounted, onUnmounted, computed, ComputedRef } from 'vue';

export enum BrowserType {
  Chrome = 'Chrome',
  Edge = 'Edge',
  Unsupported = 'Unsupported'
}

const LATEST_BROWSER_VERSIONS: Record<BrowserType, number> = {
  [BrowserType.Chrome]: 141,
  [BrowserType.Edge]: 141,
  [BrowserType.Unsupported]: 0
};

const MIN_DIMENSIONS = {
  WIDTH: 1200,
  HEIGHT: 550
};

const getBrowserInfo = (): { name: BrowserType; version: number } => {
  const userAgent = navigator.userAgent;

  const browserRegexMap = {
    [BrowserType.Chrome]: /Chrom(?:e|ium)\/([\d.]+)/i,
    [BrowserType.Edge]: /Edg\/([\d.]+)/i
  } as const;

  for (const [name, regex] of Object.entries(browserRegexMap)) {
    const match = userAgent.match(regex);
    if (match) {
      return { name: name as BrowserType, version: parseFloat(match[1]) || 0 };
    }
  }

  return { name: BrowserType.Unsupported, version: 0 };
};

export function useGetBrowserInfo() {
  const isBrowserValid = ref<boolean>(true);
  const isResolutionValid = ref<boolean>(true);
  const isBrowserCheckActive = ref<boolean>(false);

  const checkResolution = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    isResolutionValid.value = width >= MIN_DIMENSIONS.WIDTH && height >= MIN_DIMENSIONS.HEIGHT;
  };

  const shouldCheckBrowser = computed(() => {
    const environment = (import.meta as ImportMeta).env.VITE_APP_ENV;

    return environment !== 'prototyping' && environment !== 'development';
  }) as ComputedRef<boolean>;

  onMounted(() => {
    const { name, version } = getBrowserInfo();
    const latestVersion = LATEST_BROWSER_VERSIONS[name] || 0;

    isBrowserValid.value = version >= latestVersion && name !== BrowserType.Unsupported;
    isBrowserCheckActive.value = shouldCheckBrowser.value;

    checkResolution();

    window.addEventListener('resize', checkResolution);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', checkResolution);
  });

  return { isBrowserValid, isResolutionValid, isBrowserCheckActive };
}
